import { create } from 'zustand';
import type { BotDefaults } from '@/lib/bots/botDefaults';
import { parseTraderPrefs, type TraderPrefs } from '@/lib/traderPrefs';
import { resetSavedGames, useSavedGames } from './savedGames';
import { resetMinimizedGames, useMinimizedGames } from './minimizedGames';
import { resetLiveHotkeys, useLiveHotkeysStore } from './liveHotkeys';
import { resetPersonalBooks, usePersonalBooks } from './personalBooks';
import { resetSpreadTotalLineMemory } from './spreadTotalLineMemory';
import { resetSpreadTotalLineDefaults } from './spreadTotalLineDefaults';
import { resetInitialLineTally } from './initialLineTally';
import { resetWorkingViews, setWorkingViewScope, workingViewAuthScope } from './workingViewStore';
import { resetTrackerPins, useTrackerPins } from './trackerPins';
import { setPolyAttemptSession } from '../lib/infra/polyAttemptJournal';
import { bufferErrorResponse, releaseBody } from '../lib/infra/responseBody';

const TOKEN_KEY = 'g1mt_access_token';
const REFRESH_KEY = 'g1mt_refresh_token';
const USER_KEY = 'g1mt_user';

/** #990: the most the startup gate waits for GET /api/bootstrap. */
export const BOOTSTRAP_TIMEOUT_MS = 10_000;
const REFRESH_INTERVAL_MS = 11 * 60 * 60 * 1000; // refresh 1h before 12h expiry

export type UserRole = 'admin' | 'manager' | 'trader';

export interface AuthUser {
  id: string;
  username: string;
  role: UserRole;
  orgId: string | null;
  /** Hard ceiling on Σ open contracts. Null = unlimited. Source of truth is
   *  the relay /api/trader/me endpoint; login/bootstrap responses don't
   *  carry it, so it's populated by refreshMe() shortly after auth. */
  maxContracts?: number | null;
  /** Hard ceiling on count of open (ticker, side) positions. */
  maxPositions?: number | null;
  /** Whether the trader's org is enrolled in Kalshi's Market Maker Program.
   *  Source of truth is /api/trader/me; login/bootstrap don't carry it, so
   *  it's populated by refreshMe() shortly after auth. Undefined → false
   *  (not enrolled). Gates the maker-rebate display. */
  mmRebateEnabled?: boolean;
  /** Resolved bot-flow access = org.botsEnabled && this trader's botsEnabled.
   *  Source of truth /api/trader/me; populated by refreshMe(). Undefined →
   *  false. Gates every bot-flow surface (spawn, claim, bot views). */
  botAccess?: boolean;
  /** Resolved oscar overtake/blocker access (traderRules.oscarEnabled,
   *  fail-closed server-side). Source of truth /api/trader/me; populated by
   *  refreshMe(). Undefined → false. Gates every oscar surface (ticket mode,
   *  release button, settings). */
  oscarAccess?: boolean;
  /** Resolved armed one-click placement access (traderRules.oneClickEnabled,
   *  fail-closed server-side). Undefined is disabled. */
  oneClickAccess?: boolean;
  /** Resolved Live Trading V2 surface access (traderRules.lt2Enabled,
   *  fail-closed server-side, D-020). Undefined is disabled. Gates the
   *  derivative one-click surfaces, two-sided ladders, hotkeys and multi-line
   *  view here; the relay independently re-checks the same grant on every
   *  LT2-tagged order, so this field is presentation and never authority. */
  lt2Access?: boolean;
  /** Server-side per-order ceiling for `lt-` tagged orders
   *  Resolved by the relay, including defaults; null is unavailable/invalid. */
  oneClickMaxContracts?: number | null;
  /** Resolved Live Trading V3 Attack (W/E) EFFECTIVE access: true only when
   *  all prerequisite grants and a valid effective Attack ceiling are present.
   *  Undefined is disabled; the relay re-checks everything on
   *  every `__attack` order, so this field is presentation only. */
  attackAccess?: boolean;
  /** Effective server-side per-order ceiling for `__attack` tagged orders,
   *  including defaults and legacy compatibility. Null = invalid ceiling, which the
   *  relay refuses outright — never "unlimited". */
  attackMaxContracts?: number | null;
  /** This trader's personal bot-default overrides (null = none). Resolved
   *  personal → org → edge built-in at spawn. */
  botDefaults?: BotDefaults | null;
  /** Trader-owned preferences (`users.prefs`): low bandwidth mode, … From
   *  /api/trader/me; absent = defaults. */
  prefs?: TraderPrefs | null;
  /** The org's baseline bot defaults (null = none). The org layer of the
   *  spawn-config resolution. */
  orgBotDefaults?: BotDefaults | null;
  /** Set while the grant fields above are NOT confirmed by this session's
   *  /api/trader/me read: `unknown` after login (no grants yet) or reload
   *  (grants restored from storage, possibly revoked since); `error` after a
   *  failed read. Absent = confirmed. Every sign-in path in this file stamps
   *  it and refreshMe() clears it; read it through selectGrantsStatus. */
  grantsPending?: 'unknown' | 'error';
}

const unconfirmed = (user: AuthUser): AuthUser => ({ ...user, grantsPending: 'unknown' });

export type AuthStatus =
  | 'idle'
  | 'loading'
  | 'authenticated'
  | 'unauthenticated';

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  status: AuthStatus;
  needsSetup: boolean;
  error: string | null;
  /** #1113: why the last sign-out happened, shown on Sign In. Set only when a
   *  definitive refresh rejection ended the session; a manual or cross-tab
   *  sign-out leaves it null. In memory only (a reload clears it); a successful
   *  sign-in clears it. */
  signedOutReason: 'session-ended' | null;

  checkBootstrap: () => Promise<void>;
  bootstrap: (username: string, password: string) => Promise<void>;
  login: (username: string, password: string) => Promise<void>;
  refresh: () => Promise<boolean>;
  /** `reason` is passed only when the relay definitively rejected the refresh
   *  (#1113); every other sign-out calls it bare. */
  logout: (reason?: 'session-ended') => Promise<void>;
  hydrate: () => void;
  /** Re-fetches /api/trader/me and merges any updated fields (notably the
   *  maxContracts/maxPositions caps) into the persisted AuthUser. Safe to
   *  call any time after auth; no-op if logged out. Resolves `true` only when
   *  this read's profile was applied (#1113); `false` when there was no
   *  session, the read failed, or a newer read or session superseded it. */
  refreshMe: () => Promise<boolean>;
  /** Saves trader-owned preferences (`PATCH /api/trader/me/prefs`), e.g. Low
   *  bandwidth mode. Optimistic: `user.prefs` takes the patch at once (so the
   *  optional game scope switches without waiting), reconciles with the relay's echo,
   *  and rolls back if the save fails. Resolves `true` when the relay stored
   *  it; `false` on no session, a refusal, a network failure, or when a newer
   *  save or another session superseded this one. Never throws. */
  setPrefs: (patch: Partial<TraderPrefs>) => Promise<boolean>;
}

let refreshTimer: ReturnType<typeof setInterval> | null = null;
let _inflightRefresh: Promise<boolean> | null = null;
let _inflightRefreshEpoch = -1;
let _sessionEpoch = 0;
/** Read-only identity generation for async clients; token refresh does not advance it. */
export const getAuthSessionEpoch = (): number => _sessionEpoch;
let _profileReadSeq = 0;
/** Bumped by every setPrefs: the latest save owns `user.prefs` until it lands,
 *  and a profile read that started before it must not overwrite it. */
let _prefsWriteSeq = 0;
let _prefsPending: Promise<void> | null = null;
/** The prefs the relay last confirmed in this session (a /me read or a save's
 *  echo): what a failed save rolls back to, so two quick saves that both fail
 *  cannot leave the first one's optimistic value standing. */
let _confirmedPrefs: { epoch: number; prefs: TraderPrefs } | null = null;
let _crossTabBound = false;
let _loggingOut = false;

function startRefreshTimer(refreshFn: () => Promise<boolean>) {
  stopRefreshTimer();
  refreshTimer = setInterval(() => {
    refreshFn().catch(() => {});
  }, REFRESH_INTERVAL_MS);
}

function stopRefreshTimer() {
  if (refreshTimer) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
}

function persist(token: string, refreshToken: string, user: AuthUser) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(REFRESH_KEY, refreshToken);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

function clearPersisted() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
}

/** Wipe every cross-component per-user cache so a logout (in THIS tab or a
 *  sibling tab via the storage event) can't leak the previous user's
 *  positions/orders/headroom/rate-limit into the next session. Dynamic imports
 *  keep authStore free of UI-layer deps. Must be run on EVERY path to
 *  unauthenticated — both logout() and the cross-tab storage handler. */
function purgeUserCaches() {
  // Synchronous, unlike the dynamic imports below. Those defer because they
  // reach into hook modules and authStore must stay free of UI-layer deps;
  // the mark stores are leaves with no such deps, and a cross-account boundary
  // should not be left open for even a microtask. See resetSavedGames.
  resetSavedGames();
  resetMinimizedGames();
  // #725 — the key mapping decides which keystroke places an order, so the
  // next trader on this browser must never inherit it. Persisted mappings are
  // left intact; signing back in restores your own.
  resetLiveHotkeys();
  // #1287 — a trader's chosen OddsPapi books; never inherited.
  resetPersonalBooks();
  // #943 — pinned orders decide what Cancel All skips; never inherited.
  resetTrackerPins();
  resetSpreadTotalLineMemory();
  // #1071 — seeded line defaults and the initial-line count are this
  // session's too.
  resetSpreadTotalLineDefaults();
  resetInitialLineTally();
  resetWorkingViews();
  void import('../hooks/useHeadroom').then((m) => m.resetHeadroomCache()).catch(() => {});
  void import('../hooks/useUserKalshiPOS').then((m) => m.resetUserKalshiPOSCache()).catch(() => {});
  void import('../hooks/useRateLimitStatus').then((m) => m.resetRateLimitStatusCache()).catch(() => {});
  void import('../hooks/usePolymarketRelayOrders').then((m) => m.resetPolyOrdersShare()).catch(() => {});
  void import('../hooks/usePolymarketRelayPositions').then((m) => m.resetPolyPositionsSeed()).catch(() => {});
  void import('../hooks/usePolyBalance').then((m) => m.resetPolyBalanceCache()).catch(() => {});
  // #1058 — both are stamped with the admin they were read for, so these only
  // free memory; neither is what keeps a previous admin's list from showing.
  void import('./orgScope').then((m) => m.resetOrgScopeOrgs()).catch(() => {});
  void import('../views/Admin/adminPageCache').then((m) => m.resetAdminPageCache()).catch(() => {});
}

/** Reload the lists stored per USER (not per org or role) after a purge that
 *  kept the same user signed in. The shell's hydrate effects re-run only on a
 *  new user id, so without this a sibling tab's org or role change left this
 *  tab with no pins (the next Cancel All / Pull All cancels pinned orders) and
 *  no hotkeys. Synchronous, so no bulk cancel can run in between. */
function reloadUserLists(userId: string) {
  useTrackerPins.getState().hydrateFor(userId);
  useLiveHotkeysStore.getState().hydrateFor(userId);
  usePersonalBooks.getState().hydrateFor(userId);
  useSavedGames.getState().hydrateFor(userId);
  useMinimizedGames.getState().hydrateFor(userId);
}

export const useAuthStore = create<AuthState>((set, get) => ({
  token: null,
  refreshToken: null,
  user: null,
  status: 'idle',
  needsSetup: false,
  error: null,
  signedOutReason: null,

  hydrate: () => {
    // Cross-tab/window token sync: adopt token changes written by sibling tabs or
    // popout windows (/fills) so a tab never refreshes with a now-
    // stale in-memory token (the cross-context refresh storm that drove the
    // flicker incident), and so a logout in one context propagates to all. Bound
    // once per page. See project_position_flicker_incident.
    if (!_crossTabBound && typeof window !== 'undefined') {
      _crossTabBound = true;
      window.addEventListener('storage', (e) => {
        if (e.key && e.key !== TOKEN_KEY && e.key !== REFRESH_KEY && e.key !== USER_KEY) return;
        // A deliberate logout in THIS tab is in flight — don't let a sibling tab's
        // racing refresh (which may rewrite tokens) resurrect authed state in a
        // tab the user just logged out of.
        if (_loggingOut) return;
        const t = localStorage.getItem(TOKEN_KEY);
        const r = localStorage.getItem(REFRESH_KEY);
        const u = localStorage.getItem(USER_KEY);
        if (t && r && u) {
          try {
            const adopted = JSON.parse(u) as AuthUser;
            const current = get().user;
            const sameIdentity = current?.id === adopted.id && current?.orgId === adopted.orgId
              && current?.role === adopted.role;
            // A sibling tab signed in as someone else: this tab's per-user
            // caches belong to the replaced identity and must not survive it.
            if (current && !sameIdentity) {
              purgeUserCaches();
              // Same user in a new org or role: org-scoped caches go, but the
              // user's own pins and hotkeys are theirs in every org.
              if (current.id === adopted.id) reloadUserLists(adopted.id);
            }
            // Same identity: a sibling's confirmed profile is adopted as-is, an
            // unconfirmed one (its fresh login) keeps this tab's profile. A new
            // identity starts unconfirmed; the shell re-reads its profile.
            const user = !sameIdentity || !current ? unconfirmed(adopted)
              : adopted.grantsPending ? current : adopted;
            set({ token: t, refreshToken: r, user, status: 'authenticated', error: null, signedOutReason: null });
            startRefreshTimer(get().refresh); // proactively refresh however we became authed
          } catch { /* ignore malformed cross-tab payload */ }
        } else {
          stopRefreshTimer();
          // A cross-tab logout cleared the tokens — purge per-user caches here
          // too (this branch does NOT call logout()), else the shared poller
          // keeps the previous user's positions/orders cached + polling.
          purgeUserCaches();
          // #1113: a sign-out from another tab shows no reason on Sign In.
          set({ token: null, refreshToken: null, user: null, status: 'unauthenticated', signedOutReason: null });
        }
      });
    }
    const token = localStorage.getItem(TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_KEY);
    const userJson = localStorage.getItem(USER_KEY);
    if (token && refreshToken && userJson) {
      try {
        const user = unconfirmed(JSON.parse(userJson) as AuthUser);
        set({ token, refreshToken, user, status: 'authenticated', error: null });
        startRefreshTimer(get().refresh);
        // Eagerly refresh the access token — it's likely expired after page close.
        // This runs concurrently with the initial render; apiFetch 401 retries
        // will coalesce onto the same in-flight promise via _inflightRefresh.
        get().refresh().catch(() => {});
      } catch {
        clearPersisted();
        set({ status: 'unauthenticated' });
      }
    } else {
      set({ status: 'unauthenticated' });
    }
  },

  checkBootstrap: async () => {
    set({ status: 'loading', error: null });
    // #990: <App/> shows only its startup spinner until this settles, so the
    // read is bounded (the abort also covers a body that stalls). The answer
    // only says whether the server still needs first-time setup; any server
    // that issued a persisted session was set up. So a failed, slow or non-JSON
    // answer restores that session instead of holding the spinner or sending
    // the trader to Sign In; the shell's own reads then show what is down.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), BOOTSTRAP_TIMEOUT_MS);
    try {
      const res = await fetch('/api/bootstrap', { signal: controller.signal });
      if (!res.ok) throw new Error(`bootstrap ${res.status}`);
      const data = await res.json();
      set({
        needsSetup: data.needsSetup === true,
        status: data.needsSetup ? 'unauthenticated' : get().status,
      });
      if (!data.needsSetup) {
        get().hydrate();
      }
    } catch {
      get().hydrate();
      if (get().status !== 'authenticated') {
        set({ status: 'unauthenticated', error: 'Failed to check server status' });
      }
    } finally {
      clearTimeout(timer);
    }
  },

  bootstrap: async (username, password) => {
    set({ status: 'loading', error: null });
    try {
      const res = await fetch('/api/bootstrap', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        set({ status: 'unauthenticated', error: data.error ?? 'Bootstrap failed' });
        return;
      }
      const user: AuthUser = unconfirmed({
        id: data.user.id,
        username: data.user.username,
        role: data.user.role,
        orgId: data.user.orgId ?? null,
      });
      persist(data.accessToken, data.refreshToken, user);
      set({
        token: data.accessToken,
        refreshToken: data.refreshToken,
        user,
        status: 'authenticated',
        needsSetup: false,
        error: null,
        signedOutReason: null,
      });
      startRefreshTimer(get().refresh);
    } catch {
      set({ status: 'unauthenticated', error: 'Network error' });
    }
  },

  login: async (username, password) => {
    set({ status: 'loading', error: null });
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        set({ status: 'unauthenticated', error: data.error ?? 'Login failed' });
        return;
      }
      const user: AuthUser = unconfirmed({
        id: data.user.id,
        username: data.user.username,
        role: data.user.role,
        orgId: data.user.orgId ?? null,
      });
      persist(data.accessToken, data.refreshToken, user);
      set({
        token: data.accessToken,
        refreshToken: data.refreshToken,
        user,
        status: 'authenticated',
        error: null,
        signedOutReason: null,
      });
      startRefreshTimer(get().refresh);
    } catch {
      set({ status: 'unauthenticated', error: 'Network error' });
    }
  },

  refresh: async () => {
    const epoch = _sessionEpoch;
    if (_inflightRefresh && _inflightRefreshEpoch === epoch) return _inflightRefresh;

    const execute = async (): Promise<boolean> => {
      const rt = get().refreshToken;
      if (!rt) return false;
      try {
        const res = await fetch('/api/auth/refresh', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: rt }),
        });
        if (_sessionEpoch !== epoch || get().refreshToken !== rt) { releaseBody(res); return false; }
        if (!res.ok) {
          releaseBody(res);
          // Only a definitive auth rejection tears down the session. A transient
          // failure (network / 5xx relay blip) must NOT log the user out — that
          // unmounts the whole dashboard (the flicker class). Keep the session;
          // the refresh timer / next apiFetch retries.
          if (res.status === 401 || res.status === 403) {
            await get().logout('session-ended');
          }
          return false;
        }
        const data = await res.json().catch(() => null) as {
          accessToken?: unknown;
          refreshToken?: unknown;
        } | null;
        if (_sessionEpoch !== epoch || get().refreshToken !== rt) return false;
        if (
          !data
          || typeof data.accessToken !== 'string'
          || typeof data.refreshToken !== 'string'
        ) {
          await get().logout('session-ended');
          return false;
        }
        const user = get().user;
        if (user) {
          persist(data.accessToken, data.refreshToken, user);
        }
        set({ token: data.accessToken, refreshToken: data.refreshToken });
        return true;
      } catch {
        return false;
      }
    };

    const pending = execute().finally(() => {
      if (_inflightRefresh === pending) _inflightRefresh = null;
    });
    _inflightRefreshEpoch = epoch;
    _inflightRefresh = pending;
    return pending;
  },

  refreshMe: async () => {
    const { token, user } = get();
    if (!token || !user) return false;
    const epoch = _sessionEpoch;
    const seq = ++_profileReadSeq;
    const prefsSeq = _prefsWriteSeq;
    const prefsPendingAtStart = _prefsPending !== null;
    const isCurrent = () => _sessionEpoch === epoch && seq === _profileReadSeq;
    // Grant-gated controls stay non-actionable; the next shell mount retries.
    const markFailed = () => {
      const current = get().user;
      if (isCurrent() && current?.grantsPending) set({ user: { ...current, grantsPending: 'error' } });
    };
    try {
      const res = await apiFetch('/api/trader/me');
      if (!res.ok) {
        markFailed();
        return false;
      }
      const data = (await res.json()) as Partial<AuthUser>;
      // A profile read can finish after logout, account replacement or a newer
      // profile read. It must not restore the old user or pair that profile
      // with the next account's tokens.
      if (!isCurrent()) return false;
      if (prefsSeq === _prefsWriteSeq && !prefsPendingAtStart && !_prefsPending) _confirmedPrefs = { epoch, prefs: parseTraderPrefs(data.prefs) };
      const next: AuthUser = {
        ...user,
        ...(typeof data.username === 'string' ? { username: data.username } : {}),
        ...(data.role ? { role: data.role } : {}),
        orgId: data.orgId ?? user.orgId,
        maxContracts: data.maxContracts ?? null,
        maxPositions: data.maxPositions ?? null,
        mmRebateEnabled: data.mmRebateEnabled ?? false,
        botAccess: data.botAccess ?? false,
        oscarAccess: data.oscarAccess ?? false,
        oneClickAccess: data.oneClickAccess ?? false,
        lt2Access: data.lt2Access ?? false,
        oneClickMaxContracts: data.oneClickMaxContracts ?? null,
        attackAccess: data.attackAccess ?? false,
        attackMaxContracts: data.attackMaxContracts ?? null,
        botDefaults: data.botDefaults ?? null,
        // A save made while this read was in flight is newer than what the
        // relay read; keep it (setPrefs reconciles it with its own echo).
        prefs: prefsSeq === _prefsWriteSeq && !prefsPendingAtStart && !_prefsPending ? parseTraderPrefs(data.prefs) : get().user?.prefs ?? {},
        orgBotDefaults: data.orgBotDefaults ?? null,
        grantsPending: undefined,
      };
      const rt = get().refreshToken;
      const tok = get().token;
      if (tok && rt) persist(tok, rt, next);
      set({ user: next });
      return true;
    } catch {
      markFailed();
      return false;
    }
  },

  setPrefs: async (patch) => {
    const { token, user } = get();
    if (!token || !user) return false;
    const epoch = _sessionEpoch;
    const seq = ++_prefsWriteSeq;
    const before = user.prefs ?? {};
    // Serialize writes so the server cannot save an earlier toggle last.
    const previousWrite = _prefsPending;
    let finishWrite!: () => void;
    const thisWrite = new Promise<void>((resolve) => { finishWrite = resolve; });
    _prefsPending = thisWrite;
    // Only the latest save, in the same session, may touch user.prefs.
    const owns = () => _sessionEpoch === epoch && seq === _prefsWriteSeq;
    const rollback = () => apply(_confirmedPrefs?.epoch === epoch ? _confirmedPrefs.prefs : before);
    const apply = (prefs: TraderPrefs) => {
      const current = get().user;
      if (!current) return;
      const next: AuthUser = { ...current, prefs };
      const tok = get().token;
      const rt = get().refreshToken;
      // Persisted with the user, so a reload starts in the saved mode before
      // /me confirms it (and sibling tabs adopt it through the storage event).
      if (tok && rt) persist(tok, rt, next);
      set({ user: next });
    };
    apply({ ...before, ...patch });
    try {
      if (previousWrite) await previousWrite;
      if (_sessionEpoch !== epoch) return false;
      const res = await apiFetch('/api/trader/me/prefs', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prefs: patch }),
      });
      if (!res.ok) {
        releaseBody(res);
        if (owns()) rollback();
        return false;
      }
      const data = (await res.json().catch(() => null)) as { prefs?: unknown } | null;
      const echoed = data && Object.prototype.hasOwnProperty.call(data, 'prefs') ? parseTraderPrefs(data.prefs) : null;
      if (_sessionEpoch !== epoch) return false;
      if (echoed) _confirmedPrefs = { epoch, prefs: echoed };
      if (!owns()) return false;
      // The relay's merged echo wins; a reply without one keeps the optimistic value.
      if (echoed) apply(echoed);
      return true;
    } catch {
      if (owns()) rollback();
      return false;
    } finally {
      if (_prefsPending === thisWrite) _prefsPending = null;
      finishWrite();
    }
  },

  logout: async (reason) => {
    const rt = get().refreshToken;
    // Suppress cross-tab re-adopt briefly so a sibling tab's in-flight refresh
    // can't resurrect this just-logged-out session via the storage event.
    _loggingOut = true;
    setTimeout(() => { _loggingOut = false; }, 5000);
    stopRefreshTimer();
    clearPersisted();
    purgeUserCaches();
    set({
      token: null,
      refreshToken: null,
      user: null,
      status: 'unauthenticated',
      error: null,
      // Only the two refresh-rejection paths pass a reason; a click handler's
      // event can't type-check as one.
      signedOutReason: reason === 'session-ended' ? 'session-ended' : null,
    });
    if (rt) {
      fetch('/api/auth/logout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: rt }),
      }).then(releaseBody, () => {});
    }
  },
}));

// Covers login, cross-tab adoption, admin org changes and permission refreshes.
// A temporary loading state with the same known user is not a logout.
// Unknown identity cannot restore; an identity/grant change advances the epoch.
useAuthStore.subscribe((state, previous) => {
  if (
    state.user?.id !== previous.user?.id
    || state.user?.orgId !== previous.user?.orgId
    || state.user?.role !== previous.user?.role
    || (state.status === 'unauthenticated') !== (previous.status === 'unauthenticated')
  ) {
    _sessionEpoch += 1;
    // The authenticated shell can be absent during navigation/auth recovery.
    // Clear centrally, while preserving initial same-tab reload restoration.
    if (previous.user) setPolyAttemptSession(null);
    // #1071 — G1 Health promises the count resets on an account, organization
    // or role change; a profile read can change org or role in this tab
    // without the purge.
    if (previous.user) resetInitialLineTally();
  }
  if (state.status === 'unauthenticated' || state.user) {
    setWorkingViewScope(state.status === 'unauthenticated' ? null : workingViewAuthScope(state.user));
  }
});

/**
 * Authenticated fetch wrapper. Attaches JWT, retries once on 401 via refresh.
 * An error reply comes back READ INTO MEMORY (`bufferErrorResponse`): a
 * caller that ignores it can no longer leak the reply's network pipe — the
 * 2026-10-02 gbuff wedge (see lib/infra/responseBody). 2xx replies, streams
 * included, are returned untouched; read or release them.
 */
export async function apiFetch(
  path: string,
  init: RequestInit = {},
  options: { retryUnauthorized?: boolean } = {},
): Promise<Response> {
  const { token } = useAuthStore.getState();
  const epoch = _sessionEpoch;
  const headers = new Headers(init.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);

  let res = await fetch(path, { ...init, headers });

  if (options.retryUnauthorized !== false && res.status === 401 && _sessionEpoch === epoch) {
    const refreshed = await useAuthStore.getState().refresh();
    if (refreshed && _sessionEpoch === epoch) {
      const newToken = useAuthStore.getState().token;
      if (newToken) headers.set('Authorization', `Bearer ${newToken}`);
      releaseBody(res); // the 401 is replaced, never read
      res = await fetch(path, { ...init, headers });
    }
  }

  return bufferErrorResponse(res);
}
